const axios = require('axios');
const CloverApiVersion = require('@/lib/PaymentMethods/Clover/Enums/CloverApiVersion');
const LocalStorageKeys = require('@/lib/Storage/LocalStorageKeys');
const logFactory = require('@/libraries/DigiTickets/Logging/logFactory');
const { container } = require('@/container');

/**
 * @typedef {Object} CloverToken
 * @property {string} token
 * @property {string|null} expiresAt ISO 8601 date string or null if the token does not expire.
 */

class CloverAxiosInstance {
    /**
     * @param {string} type
     * @param {import('axios').AxiosRequestConfig|{baseURL: string, headers: Object.<string, string>}} config
     */
    constructor(type, config) {
        if (!type || !Object.values(CloverApiVersion).includes(type)) {
            throw new Error('CloverAxiosInstance requires a valid type.');
        }
        if (!config) {
            throw new Error('CloverAxiosInstance requires a config object.');
        }
        if (!config.hasOwnProperty('baseURL')) {
            throw new Error('CloverAxiosInstance config requires a baseURL.');
        }
        if (!config.hasOwnProperty('headers')) {
            throw new Error('CloverAxiosInstance config requires a headers object.');
        }
        this.type = type;

        /**
         * @type {import('axios').AxiosInstance}
         */
        this.axiosInstance = axios.create(config);

        this.log = logFactory('CloverAxiosInstance');
    }

    /**
     * @returns {Promise<CloverToken>}
     */
    async getNewToken() {
        return container.apiClient
            .request('GET', 'clover/token?forceRefresh=1')
            .then((response) => {
                return response.data;
            });
    }

    /**
     * @param {CloverToken} token
     * @returns {boolean}
     */
    isTokenValid(token) {
        // Token never expires (V1 token).
        if (!token.expiresAt) {
            return true;
        }

        // Check token is valid for at least 3 more minutes.
        return (new Date(token.expiresAt) - new Date()) > (3 * 60 * 1000);
    }

    /**
     * @returns {?CloverToken}
     */
    loadToken() {
        // Load an existing token from localstorage.
        // Originally the plan wasn't to store the token in localstorage, and just keep it in memory as a
        // property of this instance. However, some requests create a new instance each time, so we need to store
        // it somewhere persistent outside the class. So localstorage it is, and it has the benefit of lowering
        // the number of token requests we need to make.
        return container.localStorageDataStore.find(LocalStorageKeys.CLOVER_TOKEN);
    }

    /**
     * @param {?CloverToken} token
     */
    storeToken(token) {
        if (token) {
            // Save token to localstorage.
            // If the token has a null expiresAt we will replace that with a 30-minute expiry (just like V2 tokens).
            // This is to avoid storing a never-expiring V1 token in localstorage and the client never being migrated
            // to a V2 token. After 30 minutes the client will ask the backend for a new token, which may still be the
            // same V1 token. That's fine. At some point it will become a V2 token once they are migrated.
            if (!token.expiresAt) {
                const expiresAt = new Date();
                expiresAt.setMinutes(expiresAt.getMinutes() + 30);
                token.expiresAt = expiresAt.toISOString();
            }
            container.localStorageDataStore.persist(LocalStorageKeys.CLOVER_TOKEN, token);
        } else {
            // Remove any existing token from localstorage.
            container.localStorageDataStore.remove(LocalStorageKeys.CLOVER_TOKEN);
        }
    }

    /**
     * @param {import('axios').AxiosRequestConfig} data
     *
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async request(data) {
        /**
         * @type {CloverToken}
         */
        let token;

        const existingToken = this.loadToken();
        let existingTokenValid = false;
        if (existingToken) {
            if (this.isTokenValid(existingToken)) {
                token = existingToken;
                this.log.debug('Using existing non-expired Clover token.', token.token.slice(-10), `Expires at ${token.expiresAt}`);
                existingTokenValid = true;
            } else {
                this.log.debug('Existing Clover token is close to or past expiry. Fetching a new one.', `Expires at ${existingToken.expiresAt}`);
                // Clear the saved token because it's not valid.
                this.storeToken(null);
            }
        } else {
            this.log.debug('No existing Clover token found. Fetching a new one.');
        }

        if (!token) {
            // No existing token was found, or it was expired. Fetch a new one.
            token = await this.getNewToken();
            // Store the new token for next time.
            this.storeToken(token);
            // Log the last 10 characters of the token for debugging.
            this.log.debug('Obtained new Clover token.', token.token.slice(-10), `Expires at ${token.expiresAt}`);
        }

        // Add the Authorization header to this request.
        if (!data.hasOwnProperty('headers')) {
            data.headers = {};
        }
        data.headers.Authorization = `Bearer ${token.token}`;

        // Send the request to Clover. If it fails with a 401 error, get a new access token and retry it once.
        try {
            return await this.axiosInstance.request(data);
        } catch (error) {
            // An error was thrown - check if it's a 401.
            if (error.response?.status === 401) {
                if (existingTokenValid) {
                    // Log if a cached token that passed validation was rejected.
                    this.log.warn('Non-expired token rejected with 401. Fetching fresh token and retrying.');
                } else {
                    // Otherwise this was a fresh token that rejected. Even weirder.
                    this.log.debug('Request failed with 401. Fetching fresh token and retrying.');
                }

                // Fetch a fresh token.
                const freshToken = await this.getNewToken();
                this.storeToken(freshToken);
                this.log.debug('Obtained new Clover token after 401.', token.token.slice(-10), `Expires at ${token.expiresAt}`);

                // Update the Authorization header with the fresh token.
                data.headers.Authorization = `Bearer ${freshToken.token}`;

                // Retry the request once (if this fails, let it throw).
                return this.axiosInstance.request(data);
            }

            // Re-throw non-401 errors.
            throw error;
        }
    }

    setIdempotencyKey(value) {
        this.axiosInstance.defaults.headers['Idempotency-Key'] = value;
    }

    clearIdempotencyKey() {
        delete this.axiosInstance.defaults.headers['Idempotency-Key'];
    }

    getIdempotencyKey() {
        return this.axiosInstance.defaults.headers['Idempotency-Key'] || null;
    }

    getType() {
        return this.type;
    }
}

module.exports = CloverAxiosInstance;
